import { useMemo } from 'react';

import { useAuthUser } from 'auth/hooks';
import { AlternateVersionHelper } from 'core/helpers';
import { useAgGridTableContext } from 'core/states';
import {
  AddBomPartsToQuote,
  IAddBomPartsToQuoteProps,
  IMapperParams,
} from 'quotes/components/AddBomPartsToQuote';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { IBaseQuote } from 'quotes/types';

import { mapAddBomPartsToQuoteForm } from './mappers';
import { ItemsToCopyField } from './Steps/SelectItemsToCopy/enums';
import { ItemsToCopyFormFields } from './Steps/SelectItemsToCopy/ItemsToCopyFormFields';
import { getSchema } from './Steps/SelectItemsToCopy/schema';
import {
  ICreateCustomerQuoteFromJobForm,
  ICreateVendorQuoteFromJobForm,
} from './Steps/SelectItemsToCopy/types';

interface IProps {
  addToQuoteType: QuoteType;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

export const AddToQuote = ({ addToQuoteType, onClose, onSubmitSuccess }: IProps) => {
  const { selectedRows: jobParts } = useAgGridTableContext();

  const { user } = useAuthUser();
  const { addBomPartsToCustomerQuote, addBomPartsToVendorQuote } = useQuoteActions();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const itemsToCopyValues = Object.values(ItemsToCopyField);

  const mapperFunction = useMemo(
    () =>
      (params: IMapperParams<ICreateCustomerQuoteFromJobForm | ICreateVendorQuoteFromJobForm>) => {
        return mapAddBomPartsToQuoteForm({
          targetQuote: params.targetQuote,
          toQuoteType: params.toQuoteType,
          data: params.data,
          jobParts: params.selectedItems,
          isAlternateVersionEnabled: params.isAlternateVersionEnabled,
        });
      },
    []
  );

  const addBomPartsToQuoteProps: IAddBomPartsToQuoteProps<
    ICreateCustomerQuoteFromJobForm | ICreateVendorQuoteFromJobForm
  > = {
    addToQuoteType,
    onClose,
    onSubmitSuccess,
    selectedItems: jobParts,
    entityType: QuoteEntityType.QUOTE,
    mapperFunction,
    addToCustomerQuoteAction: addBomPartsToCustomerQuote,
    addToVendorQuoteAction: addBomPartsToVendorQuote,
    cliJobInProgressMessageKey: 'messagePartsAddingInProgress',
    cliJobSuccessMessageKey: 'messagePartsAddingSuccess',
    itemsToCopyProps: {
      getSchema: (selectedQuote?: IBaseQuote) =>
        getSchema({
          selectedQuoteId: selectedQuote?.id,
          addToQuoteType,
          itemsToCopyValues,
          companySettings: user.companySettings,
          isAlternateVersionEnabled,
        }),
      fields: <ItemsToCopyFormFields itemsToCopyValues={itemsToCopyValues} />,
    },
  };

  return (
    <AddBomPartsToQuote<ICreateCustomerQuoteFromJobForm | ICreateVendorQuoteFromJobForm>
      {...addBomPartsToQuoteProps}
    />
  );
};
